import { useRef, useEffect, useState, useCallback } from 'react';
import { FiBold, FiItalic, FiUnderline, FiLink } from 'react-icons/fi';
import styles from './RichTextEditor.module.css';

const FONT_SIZES = [
  { label: 'Small', value: '12px' },
  { label: 'Normal', value: '14px' },
  { label: 'Large', value: '18px' },
  { label: 'X-Large', value: '22px' },
];

const HEADINGS = [
  { label: 'Normal', value: 'div' },
  { label: 'Heading 2', value: 'h2' },
  { label: 'Heading 3', value: 'h3' },
  { label: 'Heading 4', value: 'h4' },
];

const RichTextEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);
  const savedRangeRef = useRef(null);
  const [activeFormats, setActiveFormats] = useState({ bold: false, italic: false, underline: false });

  // Sync external value to DOM without disturbing cursor
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== (value || '')) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const handleInput = () => {
    onChange(editorRef.current.innerHTML);
  };

  const updateActiveFormats = () => {
    setActiveFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
    });
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    if (!savedRangeRef.current) return;
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(savedRangeRef.current);
  };

  const exec = useCallback((cmd, val = null) => {
    editorRef.current.focus();
    document.execCommand(cmd, false, val);
    onChange(editorRef.current.innerHTML);
    updateActiveFormats();
  }, [onChange]);

  const applyFontSize = (px) => {
    editorRef.current.focus();
    // execCommand('fontSize') wraps in <font size="7">; we replace with <span>
    document.execCommand('fontSize', false, '7');
    editorRef.current.querySelectorAll('font[size="7"]').forEach((font) => {
      const span = document.createElement('span');
      span.style.fontSize = px;
      span.innerHTML = font.innerHTML;
      font.parentNode.replaceChild(span, font);
    });
    onChange(editorRef.current.innerHTML);
  };

  const applyLink = useCallback(() => {
    editorRef.current.focus();
    const url = window.prompt('Enter URL:');
    if (url && url.trim()) {
      document.execCommand('createLink', false, url.trim());
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const isEmpty = !value || value.replace(/<[^>]+>/g, '').trim() === '';

  return (
    <div className={styles.wrapper}>
      <div className={styles.toolbar}>
        <select
          className={styles.select}
          defaultValue="div"
          onMouseDown={saveSelection}
          onChange={(e) => {
            editorRef.current.focus();
            restoreSelection();
            document.execCommand('formatBlock', false, e.target.value);
            onChange(editorRef.current.innerHTML);
            updateActiveFormats();
          }}
        >
          {HEADINGS.map((h) => (
            <option key={h.value} value={h.value}>{h.label}</option>
          ))}
        </select>

        <select
          className={styles.select}
          value=""
          onMouseDown={saveSelection}
          onChange={(e) => {
            if (!e.target.value) return;
            editorRef.current.focus();
            restoreSelection();
            applyFontSize(e.target.value);
          }}
        >
          <option value="" disabled>Size</option>
          {FONT_SIZES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>

        <div className={styles.divider} />

        <button
          type="button"
          className={`${styles.toolBtn} ${activeFormats.bold ? styles.active : ''}`}
          onMouseDown={(e) => { e.preventDefault(); exec('bold'); }}
          title="Bold"
        >
          <FiBold size={14} />
        </button>

        <button
          type="button"
          className={`${styles.toolBtn} ${activeFormats.italic ? styles.active : ''}`}
          onMouseDown={(e) => { e.preventDefault(); exec('italic'); }}
          title="Italic"
        >
          <FiItalic size={14} />
        </button>

        <button
          type="button"
          className={`${styles.toolBtn} ${activeFormats.underline ? styles.active : ''}`}
          onMouseDown={(e) => { e.preventDefault(); exec('underline'); }}
          title="Underline"
        >
          <FiUnderline size={14} />
        </button>

        <div className={styles.divider} />

        <label
          className={styles.colorLabel}
          title="Text colour"
          onMouseDown={saveSelection}
        >
          <input
            type="color"
            className={styles.colorInput}
            onChange={(e) => {
              editorRef.current.focus();
              restoreSelection();
              document.execCommand('foreColor', false, e.target.value);
              // Chrome wraps in <font color="..."> which the sanitiser strips; convert to <span>
              editorRef.current.querySelectorAll('font[color]').forEach((font) => {
                const span = document.createElement('span');
                span.style.color = font.getAttribute('color');
                span.innerHTML = font.innerHTML;
                font.parentNode.replaceChild(span, font);
              });
              onChange(editorRef.current.innerHTML);
              updateActiveFormats();
            }}
            defaultValue="#000000"
          />
          <span className={styles.colorIcon}>A</span>
        </label>

        <button
          type="button"
          className={styles.toolBtn}
          onMouseDown={(e) => { e.preventDefault(); applyLink(); }}
          title="Insert link"
        >
          <FiLink size={14} />
        </button>
      </div>

      <div className={styles.editorArea}>
        {isEmpty && placeholder && (
          <div className={styles.placeholder}>{placeholder}</div>
        )}
        <div
          ref={editorRef}
          className={styles.editor}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onKeyUp={updateActiveFormats}
          onMouseUp={updateActiveFormats}
        />
      </div>
    </div>
  );
};

export default RichTextEditor;
